import React, { useMemo, useState } from "react";
import submit from "submit-form";
import PropTypes from "prop-types";
import SvgIcon from "../../../../parts/SvgIcon";
import Modal from "../../../../parts/modal-new";
import OptionToggler from "../../../../parts/OptionToggler";
import { SearchDropdown } from "../../../../parts/inputs/search-dropdown";
import { getDateToDisplay } from "../../../../utils/nutrition-helpers";

const CopyMealPlanFromModal = ({ locals }) => {
  const {
    activeUserToRequest,
    mealPlanTemplates = [],
    query,
    assignedMealPlans = [],
    allAthletesFromOrganization = [],
    activeUser: { athletes = [] },
  } = locals;
  const athleteLabel = referAsClients ? "Select client" : "Select athlete";
  const { templateId = "" } = query;
  const { referAsClients } = activeUserToRequest;

  const filteredMealPlans = mealPlanTemplates.filter(
    (template) => template._id !== templateId
  );

  const [state, setState] = useState({
    selectedOption: "template",
    selectedWeeks: [],
    selectedAthlete: null,
    selectedPlan: null,
  });

  const { selectedOption, selectedWeeks, selectedAthlete, selectedPlan } =
    state;

  const togglerOptions = [
    { value: "template", label: "From Template" },
    {
      value: "athlete",
      label: `From Another ${referAsClients ? "Client" : "Athlete"}`,
    },
  ];
  const mealPlanOptions = filteredMealPlans.map(
    ({ _id, title, weeks = [] }) => ({
      value: _id,
      label: title,
      weeks,
    })
  );

  const athletesOptions = useMemo(() => {
    const assignedAthleteIds = new Set(
      assignedMealPlans.map(({ athleteId }) => athleteId)
    );

    const activeAthletes = athletes
      .filter(
        ({ status, _id }) => !status?.inactive && assignedAthleteIds.has(_id)
      )
      .map(({ _id, name, avatar }) => ({
        value: _id,
        label: name,
        avatar: avatar,
      }));

    const activeOrgAthletes = allAthletesFromOrganization
      .filter(
        ({ athleteStatus, athleteId }) =>
          !athleteStatus?.inactive && assignedAthleteIds.has(athleteId)
      )
      .map(({ athleteId, athleteName, athleteAvatar }) => ({
        value: athleteId,
        label: athleteName,
        avatar: athleteAvatar,
      }));

    const uniqueAthletes = new Map(
      [...activeAthletes, ...activeOrgAthletes].map((item) => [
        item.value,
        item,
      ])
    );

    return Array.from(uniqueAthletes.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [athletes, allAthletesFromOrganization, assignedMealPlans]);

  const searchOptions =
    selectedOption === "template"
      ? mealPlanOptions
      : selectedOption === "athlete" && selectedAthlete
      ? assignedMealPlans
          .filter(({ athleteId }) => athleteId === selectedAthlete.value)
          .map(
            ({
              _id: mealPlanId,
              utcStartDate,
              utcEndDate,
              weeks: mealPlanWeeks = [],
            }) => ({
              value: mealPlanId,
              label: `${getDateToDisplay(utcStartDate)} - ${getDateToDisplay(
                utcEndDate
              )}`,
              weeks: mealPlanWeeks,
            })
          )
      : [];

  /**
   * Function to update the state
   * @param {*} newValues
   */
  const updateState = (newValues) => {
    setState((prevState) => ({ ...prevState, ...newValues }));
  };

  /**
   * Handle the selected weeks
   * @param {*} index
   */
  const handleSelectionChange = (index) => {
    updateState({
      selectedWeeks: selectedWeeks.includes(index)
        ? selectedWeeks.filter((week) => week !== index)
        : [...selectedWeeks, index],
    });
  };

  /**
   * Send the information to the api
   */
  const handleCopy = () => {
    const url = `/actions/meal-planner/copy-to-from-meal-template`;

    const body = {
      toPlanId: templateId,
      fromPlanId: selectedPlan?.value,
      fromSelectedWeeks: selectedWeeks,
      handle: "from",
    };
    submit(url, {
      method: "POST",
      body,
    });
  };

  return (
    <Modal
      toClear={["templateId"]}
      className="fetch-modal copy-meal-plan-from"
      title="Copy Meal Plan From"
    >
      <div className="body">
        <OptionToggler
          name="fetchType"
          options={togglerOptions}
          defaultValue={selectedOption}
          onSelectFn={(value) =>
            updateState({
              selectedOption: value,
              selectedWeeks: [],
              selectedAthlete: null,
              selectedPlan: null,
            })
          }
        />
        {selectedOption === "athlete" && (
          <>
            <label>{athleteLabel} to copy from</label>

            <SearchDropdown
              name="athletes"
              options={athletesOptions}
              handleChange={(value) =>
                updateState({
                  selectedAthlete: value,
                  selectedPlan: null,
                  selectedWeeks: [],
                })
              }
              value={selectedAthlete?.label}
              valueKey="label"
              placeholder={athleteLabel}
            />
          </>
        )}
        {selectedOption === "template" ||
        (selectedOption === "athlete" && selectedAthlete) ? (
          <>
            <label>Select Meal Plan</label>

            <SearchDropdown
              name="plans"
              options={searchOptions}
              handleChange={(value) => updateState({ selectedPlan: value })}
              value={selectedPlan?.label}
              valueKey="label"
              placeholder="Select meal plan"
            />
          </>
        ) : null}
        {selectedPlan && (
          <>
            <div className="weeks-container">
              <label>Select Week</label>
              <div className="week-list">
                <ul>
                  {selectedPlan?.weeks.map(({}, index) => (
                    <li key={`${selectedPlan?._id}-${index}`}>
                      <SvgIcon
                        onClick={() => handleSelectionChange(index)}
                        src={`/img/new-icons/${
                          selectedWeeks.includes(index)
                            ? "checked-square.svg"
                            : "unchecked-square.svg"
                        }`}
                        noFilterOnLight={selectedWeeks.includes(index)}
                      />
                      <span>Week {index + 1}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="group fetch-program-summary">
              <p className="chip selected-info">
                The selected weeks of Selected Meal Plan will be added to
                current/active week
              </p>
            </div>
          </>
        )}
      </div>

      <footer>
        <a
          role="button"
          data-button-type="secondary"
          data-action="cancel"
          href="/actions/refresh?modal=&sourceOwner=&templateId="
        >
          Cancel
        </a>
        <button
          type="submit"
          data-button-type="primary"
          disabled={selectedWeeks.length === 0}
          onClick={handleCopy}
        >
          Add Meal Plan
        </button>
      </footer>
    </Modal>
  );
};

CopyMealPlanFromModal.propTypes = {
  locals: PropTypes.object,
};

export default CopyMealPlanFromModal;
